import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export type AdapterType = 'privy-embedded'
export type WalletType = 'Privy Embedded'
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected'

const STORAGE_KEY = 'nomadian_wallet'

interface PersistedWalletState {
    walletAddress: string | null
    adapterType: AdapterType | null
    walletType: WalletType | null
    privyWalletPubkey: string | null
    jwt: string | null
    userId: string | null
    sessionExpiry: number | null
}

function loadPersistedState(): PersistedWalletState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        return JSON.parse(raw) as PersistedWalletState
    } catch {
        return null
    }
}

function persistState(state: PersistedWalletState): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function clearPersistedState(): void {
    localStorage.removeItem(STORAGE_KEY)
}

export const useWalletStore = defineStore('wallet', () => {
    const persisted = loadPersistedState()

    const walletAddress = ref<string | null>(persisted?.walletAddress ?? null)
    const adapterType = ref<AdapterType | null>(persisted?.adapterType ?? null)
    const walletType = ref<WalletType | null>(persisted?.walletType ?? null)
    const privyWalletPubkey = ref<string | null>(persisted?.privyWalletPubkey ?? null)
    const connectionStatus = ref<ConnectionStatus>(
        persisted?.walletAddress ? 'connected' : 'disconnected'
    )
    const jwt = ref<string | null>(persisted?.jwt ?? null)
    const userId = ref<string | null>(persisted?.userId ?? null)
    const sessionExpiry = ref<number | null>(persisted?.sessionExpiry ?? null)

    const isConnected = computed(() => connectionStatus.value === 'connected')
    const isAuthenticated = computed(() => !!jwt.value && !isSessionExpired.value)
    const isSessionExpired = computed(() => {
        if (!sessionExpiry.value) return true
        return Date.now() > sessionExpiry.value
    })
    const truncatedAddress = computed(() => {
        if (!walletAddress.value) return ''
        const addr = walletAddress.value
        return `${addr.slice(0, 4)}...${addr.slice(-4)}`
    })

    watch(
        [walletAddress, adapterType, walletType, privyWalletPubkey, jwt, userId, sessionExpiry],
        () => {
            persistState({
                walletAddress: walletAddress.value,
                adapterType: adapterType.value,
                walletType: walletType.value,
                privyWalletPubkey: privyWalletPubkey.value,
                jwt: jwt.value,
                userId: userId.value,
                sessionExpiry: sessionExpiry.value,
            })
        },
        { deep: true }
    )

    function connect(
        address: string,
        adapter: AdapterType,
        type: WalletType
    ) {
        walletAddress.value = address
        adapterType.value = adapter
        walletType.value = type
        connectionStatus.value = 'connected'
    }

    function setPrivyWalletPubkey(pubkey: string) {
        privyWalletPubkey.value = pubkey
    }

    function setAuth(token: string, uid: string, expiresInMs = 30 * 60 * 1000) {
        jwt.value = token
        userId.value = uid
        sessionExpiry.value = Date.now() + expiresInMs
    }

    function refreshSession(expiresInMs = 30 * 60 * 1000) {
        if (jwt.value) {
            sessionExpiry.value = Date.now() + expiresInMs
        }
    }

    /**
     * Full sign-out: resets wallet state, clears ALL related stores,
     * signs out of auth providers, and purges browser storage.
     * Every sign-out entry point (sidebar, header, profile, session timeout)
     * should call ONLY this function — no extra cleanup needed.
     */
    async function disconnect() {
        // 1. Reset wallet state
        walletAddress.value = null
        adapterType.value = null
        walletType.value = null
        privyWalletPubkey.value = null
        connectionStatus.value = 'disconnected'
        jwt.value = null
        userId.value = null
        sessionExpiry.value = null
        clearPersistedState()

        // 2. Reset all related Pinia stores
        try {
            const { useUserStore } = await import('@/stores/user')
            useUserStore().reset()
        } catch { /* store may not be initialized */ }

        try {
            const { useOnboardingStore } = await import('@/stores/onboarding')
            useOnboardingStore().reset()
        } catch { /* store may not be initialized */ }

        try {
            const { useAuthorizationStore } = await import('@/stores/authorization')
            useAuthorizationStore().clearRole()
        } catch { /* store may not be initialized */ }

        try {
            const { useProjectStore } = await import('@/stores/project')
            useProjectStore().reset()
        } catch { /* store may not be initialized */ }

        try {
            const { usePortfolioStore } = await import('@/stores/portfolio')
            usePortfolioStore().reset()
        } catch { /* store may not be initialized */ }

        // 3. Sign out of Supabase (invalidates server session)
        try {
            const { supabase } = await import('@/lib/supabase')
            await supabase.auth.signOut()
        } catch { /* continue even if Supabase signout fails */ }

        // 4. Clear Privy state
        try {
            const { usePrivyAuth } = await import('@/composables/usePrivyAuth')
            usePrivyAuth().logout()
        } catch { /* continue even if Privy logout fails */ }

        // 5. Purge all browser storage
        localStorage.clear()
        sessionStorage.clear()
    }

    function setConnecting() {
        connectionStatus.value = 'connecting'
    }

    return {
        walletAddress,
        adapterType,
        walletType,
        privyWalletPubkey,
        connectionStatus,
        jwt,
        userId,
        sessionExpiry,
        isConnected,
        isAuthenticated,
        isSessionExpired,
        truncatedAddress,
        connect,
        setPrivyWalletPubkey,
        setAuth,
        refreshSession,
        disconnect,
        setConnecting,
    }
})
